/**
 * `useTaskSnapshots` — the data layer for B3.4 named snapshots (issue #765,
 * the "deeper time machine"). Three thin hooks over the already-merged
 * snapshot endpoints:
 *
 *     GET    /api/teams/{teamId}/tasks/snapshots            → { snapshots: [] }
 *     POST   /api/teams/{teamId}/tasks/snapshots            → 201 { snapshot }
 *     GET    /api/teams/{teamId}/tasks/snapshots/{id}       → { snapshot, nodes: NodeStateAsOf[] }
 *
 * A snapshot is a LABEL on a past instant — "v1 submitted to ethics",
 * "post-review plan". Opening one returns the tree exactly as it stood at the
 * snapshot's `at_timestamp` (the SAME `project_as_of` the rewind uses), so the
 * snapshot-open surface reuses the #764 read-only as-of canvas verbatim
 * (`adaptAsOfNodes` → `buildTree` → `<TaskCanvas readOnly/>`).
 *
 * The clean query/view split (§A7): this module owns fetching + caching +
 * cache-invalidation; the views consume the hooks and never call `apiFetch`.
 * Creating a snapshot invalidates the list so the new row appears with no
 * manual refetch (the mutation contract the brief asks for).
 *
 * Graceful degradation: the endpoints may not be deployed in a given
 * environment yet. `retry: false` + an exposed `isError` lets the view render
 * a calm "Snapshots aren't available yet" state rather than crash or spin. A
 * non-array body degrades to an empty list.
 *
 * Brand firewall (§A9): a snapshot bookmarks the PLAN's state at an instant.
 * `created_by` is shown only as light provenance ("pinned by AT"), never an
 * aggregate or tally about a person.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema types
 * — no scheduler coupling (`taskmap ⊥ scheduler`).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

export type TaskSnapshot = components["schemas"]["TaskSnapshotRead"];
type SnapshotListResponse = components["schemas"]["SnapshotListResponse"];
type SnapshotResponse = components["schemas"]["SnapshotResponse"];
type OpenSnapshotResponse = components["schemas"]["OpenSnapshotResponse"];

/** Cache key for a team's snapshot list. Exported for tests + invalidation. */
export const SNAPSHOTS_KEY = (teamId: string) => ["task-snapshots", teamId] as const;
/** Cache key for one opened snapshot (its row + the as-of tree). */
export const SNAPSHOT_OPEN_KEY = (teamId: string, snapshotId: string) =>
  ["task-snapshot-open", teamId, snapshotId] as const;

const STALE_TIME_MS = 60_000;

function snapshotsBase(teamId: string): string {
  return `/api/teams/${teamId}/tasks/snapshots`;
}

export interface UseTaskSnapshotsResult {
  /** The team's curated snapshots, newest-pinned first (server order). Empty while loading / on error. */
  readonly snapshots: TaskSnapshot[];
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

/**
 * The team's named snapshots — the curated "moments worth returning to."
 * `enabled` only when a team is present. Degrades to an empty list on a
 * non-array / errored body so the view shows the calm empty/unavailable state.
 */
export function useTaskSnapshots(teamId: string | undefined): UseTaskSnapshotsResult {
  const query = useQuery({
    queryKey: SNAPSHOTS_KEY(teamId ?? ""),
    enabled: Boolean(teamId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<SnapshotListResponse>(snapshotsBase(teamId as string)),
  });

  const snapshots = Array.isArray(query.data?.snapshots) ? query.data.snapshots : [];
  return { snapshots, isLoading: query.isLoading, isError: query.isError };
}

export interface CreateSnapshotArgs {
  /** The human label — "v1 submitted to ethics". */
  label: string;
  /** ISO instant to pin; omitted ⇒ the server pins the current state ("now"). */
  atTimestamp?: string | null | undefined;
}

/**
 * Pin a named snapshot. On success the team's snapshot list is invalidated so
 * the new bookmark appears without a manual refetch. Returns the full
 * `UseMutationResult` so the view can read `isPending` for the inline form.
 */
export function useCreateTaskSnapshot(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ label, atTimestamp }: CreateSnapshotArgs) =>
      apiFetch<SnapshotResponse>(snapshotsBase(teamId as string), {
        method: "POST",
        body: JSON.stringify({
          label,
          // Only send `at_timestamp` when the caller chose a past instant;
          // omitting it lets the server pin "now" (its documented default).
          ...(atTimestamp ? { at_timestamp: atTimestamp } : {}),
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SNAPSHOTS_KEY(teamId ?? "") }),
  });
}

export interface UseOpenSnapshotResult {
  /** The snapshot row (label + instant + provenance), or null. */
  readonly snapshot: TaskSnapshot | null;
  /** The tree as it stood at the snapshot's instant (replay shape). Empty while loading / on error. */
  readonly nodes: components["schemas"]["NodeStateAsOf"][];
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

/**
 * Open one snapshot: fetch its row + the tree as it stood at its instant. The
 * server runs the SAME `project_as_of(at_timestamp)` the rewind uses, so the
 * `nodes` are the very `NodeStateAsOf[]` shape the #764 as-of adapter consumes
 * — the snapshot-open surface renders through the identical read-only canvas.
 * `enabled` only when both a team + a chosen snapshot id are present.
 */
export function useOpenSnapshot(
  teamId: string | undefined,
  snapshotId: string | undefined,
): UseOpenSnapshotResult {
  const query = useQuery({
    queryKey: SNAPSHOT_OPEN_KEY(teamId ?? "", snapshotId ?? ""),
    enabled: Boolean(teamId) && Boolean(snapshotId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () =>
      apiFetch<OpenSnapshotResponse>(`${snapshotsBase(teamId as string)}/${snapshotId}`),
  });

  const nodes = Array.isArray(query.data?.nodes) ? query.data.nodes : [];
  return {
    snapshot: query.data?.snapshot ?? null,
    nodes,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
